/**
 * Dealer float top-ups — the bank-slip review queue for super dealers.
 *
 * Layout follows the review-queue pattern researched in Mobbin (QuickBooks
 * "Receipts / For review / Reviewed" plus Deel's split-panel receipt viewer):
 * a tabbed queue with counts on the left, and the evidence in the drawer above
 * the claim it is supposed to prove.
 *
 * Nothing here credits a wallet directly. Approving calls the API, which posts
 * the ledger entry and the audit event together.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { EmptyState, ErrorCard, LoadingCard } from "../components/Async";
import { useAdminQuery, useDebouncedValue, shortDateTime, errorMessage, tzs } from "../lib/admin";
import { adminApi } from "../lib/api";
import { fetchEvidenceUrl, releaseEvidenceUrl } from "../lib/evidence";
import {
  AlertTriangle,
  Banknote,
  Check,
  Clock,
  Search,
  ShieldAlert,
  X,
} from "lucide-react";

type Tab = "pending" | "approved" | "rejected";

interface DealerRef {
  code: string;
  business_name: string;
  phone: string;
  state: string;
}

interface TopupRequest {
  id: string;
  reference: string;
  amount_minor: number;
  credited_amount_minor: number | null;
  state: string;
  account_id: string | null;
  payer_name: string | null;
  bank_reference: string | null;
  paid_at: string | null;
  note: string | null;
  file_mime: string;
  file_size_bytes: number;
  original_name: string | null;
  has_slip: boolean;
  decision_reason: string | null;
  funding_ref: string | null;
  created_at: string;
  decided_at: string | null;
  dealer?: DealerRef | null;
}

interface TopupList {
  data: TopupRequest[];
  total: number;
  counts: Record<string, number>;
  pending_amount_minor: number;
}

function statusTone(state: string) {
  if (state === "approved") return "ev-badge ev-badge-mint";
  if (state === "rejected") return "ev-badge ev-badge-red";
  if (state === "cancelled") return "ev-badge";
  return "ev-badge ev-badge-amber";
}

/** The slip itself: an image previews inline, a PDF needs a frame. */
function Slip({ request }: { request: TopupRequest }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    setUrl(null);
    setError(null);
    if (!request.has_slip) {
      setError("This slip was purged after the retention window.");
      return;
    }
    fetchEvidenceUrl(`/admin/v1/dealer-topups/${request.id}/slip`)
      .then((created) => {
        objectUrl = created;
        setUrl(created);
      })
      .catch((err: unknown) => setError(errorMessage(err)));
    return () => {
      releaseEvidenceUrl(objectUrl);
    };
  }, [request.id, request.has_slip]);

  return (
    <div className="topup-slip">
      {error && <p className="ev-body-micro muted">{error}</p>}
      {!error && !url && <LoadingCard label="Opening the bank slip…" />}
      {url && request.file_mime === "application/pdf" && (
        <iframe
          className="topup-slip-frame"
          src={url}
          title={`Bank slip ${request.reference}`}
        />
      )}
      {url && request.file_mime !== "application/pdf" && (
        <a className="topup-slip-link" href={url} target="_blank" rel="noreferrer">
          <img className="topup-slip-image" src={url} alt={`Bank slip ${request.reference}`} />
          <span className="ev-body-micro muted">Tap to open full size</span>
        </a>
      )}
    </div>
  );
}

export function DealerTopups() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("pending");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<TopupRequest | null>(null);
  const [creditAmount, setCreditAmount] = useState<string>("");
  const [reason, setReason] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  const q = useDebouncedValue(search);
  const list = useAdminQuery<TopupList>(["dealer-topups", tab, q], "/admin/v1/dealer-topups", {
    state: tab,
    q: q || undefined,
  });

  const decide = useMutation({
    mutationFn: (input: { id: string; decision: "approve" | "reject" }) =>
      input.decision === "approve"
        ? adminApi.post(`/admin/v1/dealer-topups/${input.id}/approve`, {
            credit_amount_minor: creditAmount ? Math.round(Number(creditAmount) * 100) : undefined,
            reason,
          })
        : adminApi.post(`/admin/v1/dealer-topups/${input.id}/reject`, { reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      void queryClient.invalidateQueries({ queryKey: ["dealer-topups"] });
      setSelected(null);
      setReason("");
      setCreditAmount("");
      setActionError(null);
    },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const openReview = (request: TopupRequest) => {
    setSelected(request);
    setReason("");
    setActionError(null);
    setCreditAmount((request.amount_minor / 100).toFixed(0));
  };

  const counts = list.data?.counts;
  const pending = counts?.pending ?? 0;
  const claimedMinor = Number(creditAmount || 0) * 100;
  const amountDiffers =
    Boolean(creditAmount) && !Number.isNaN(claimedMinor) && claimedMinor !== selected?.amount_minor;

  return (
    <>
      <PageHead
        title="Dealer Float Top-ups"
        description="Bank transfers reported by super dealers. Approving a verified slip credits their wallet."
        actions={
          pending ? (
            <span className="ev-badge ev-badge-amber">
              <Clock size={12} /> {pending} waiting · {tzs(list.data?.pending_amount_minor ?? 0)}
            </span>
          ) : undefined
        }
      />

      <div className="admin-card-header admin-filter-bar" style={{ marginBottom: 16 }}>
        <div className="admin-toggle-group">
          {(["pending", "approved", "rejected"] as const).map((t) => (
            <button
              key={t}
              className={`admin-toggle ${tab === t ? "admin-toggle-active" : ""}`}
              onClick={() => {
                setTab(t);
                setSelected(null);
              }}
            >
              {t === "pending" ? "For review" : t === "approved" ? "Credited" : "Rejected"}
              {counts ? ` (${counts[t] ?? 0})` : ""}
            </button>
          ))}
        </div>
        <div style={{ position: "relative" }}>
          <Search
            size={16}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--ev-slate-400)",
              pointerEvents: "none",
            }}
          />
          <input
            className="ev-input"
            placeholder="Search reference, dealer or bank ref…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: 36, width: 300 }}
          />
        </div>
      </div>

      {actionError && (
        <div className="admin-note" style={{ marginBottom: 12 }}>
          <AlertTriangle size={16} />
          <span>{actionError}</span>
        </div>
      )}

      {list.isLoading && <LoadingCard label="Loading the top-up queue…" />}
      {list.error && <ErrorCard error={list.error} onRetry={() => void list.refetch()} />}
      {list.data && list.data.data.length === 0 && (
        <EmptyState>
          {tab === "pending"
            ? "Nothing waiting. Dealer bank slips land here as they are uploaded."
            : `No ${tab} top-ups.`}
        </EmptyState>
      )}

      {list.data && list.data.data.length > 0 && (
        <div className="admin-card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th className="admin-table-th">Reference</th>
                  <th className="admin-table-th">Dealer</th>
                  <th className="admin-table-th">Claimed</th>
                  <th className="admin-table-th">Bank details</th>
                  <th className="admin-table-th">Uploaded</th>
                  <th className="admin-table-th">Status</th>
                  <th className="admin-table-th" />
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((request) => (
                  <tr key={request.id} className="admin-table-row-hover">
                    <td className="admin-table-td" data-label="Reference">
                      <span className="ev-body-mono">{request.reference}</span>
                    </td>
                    <td className="admin-table-td" data-label="Dealer">
                      <div className="admin-cell-stack">
                        <span className="ev-body-semibold">{request.dealer?.business_name ?? "—"}</span>
                        <span className="ev-body-micro muted">{request.dealer?.code}</span>
                      </div>
                    </td>
                    <td className="admin-table-td" data-label="Claimed">
                      <span className="ev-body-mono">{tzs(request.amount_minor)}</span>
                      {request.credited_amount_minor !== null &&
                        request.credited_amount_minor !== request.amount_minor && (
                          <div className="ev-body-micro muted">
                            credited {tzs(request.credited_amount_minor)}
                          </div>
                        )}
                    </td>
                    <td className="admin-table-td" data-label="Bank details">
                      <div className="admin-cell-stack">
                        <span className="ev-body-micro">{request.bank_reference ?? "no bank ref"}</span>
                        <span className="ev-body-micro muted">
                          {request.account_id ?? "account not stated"}
                        </span>
                      </div>
                    </td>
                    <td className="admin-table-td" data-label="Uploaded">
                      <span className="ev-body-micro">{shortDateTime(request.created_at)}</span>
                    </td>
                    <td className="admin-table-td" data-label="Status">
                      <span className={statusTone(request.state)}>{request.state}</span>
                    </td>
                    <td className="admin-table-td" data-label="">
                      <button
                        className="ev-btn ev-btn-sm ev-btn-ghost"
                        onClick={() => openReview(request)}
                      >
                        <Banknote size={16} /> Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selected && (
        <div className="admin-drawer-overlay" onClick={() => setSelected(null)}>
          <div
            className="admin-drawer"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 560, maxWidth: "100%" }}
          >
            <div
              style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
            >
              <div>
                <h3 style={{ marginBottom: 4 }}>
                  {selected.dealer?.business_name ?? "Dealer"} · {tzs(selected.amount_minor)}
                </h3>
                <span className="ev-body-micro muted">
                  {selected.reference} · {selected.dealer?.code}
                </span>
              </div>
              <button
                className="ev-icon-btn"
                onClick={() => setSelected(null)}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <h4 style={{ marginTop: 20, marginBottom: 8 }}>Proof of payment</h4>
            <Slip request={selected} />

            <div className="admin-detail-grid" style={{ marginTop: 20 }}>
              <div>
                <div className="ev-body-micro muted">Claimed</div>
                <div className="ev-body-mono">{tzs(selected.amount_minor)}</div>
              </div>
              <div>
                <div className="ev-body-micro muted">Paid on</div>
                <div className="ev-body">{selected.paid_at ? shortDateTime(selected.paid_at) : "—"}</div>
              </div>
              <div>
                <div className="ev-body-micro muted">Account paid into</div>
                <div className="ev-body">{selected.account_id ?? "—"}</div>
              </div>
              <div>
                <div className="ev-body-micro muted">Bank reference</div>
                <div className="ev-body-mono">{selected.bank_reference ?? "—"}</div>
              </div>
              <div>
                <div className="ev-body-micro muted">Payer name</div>
                <div className="ev-body">{selected.payer_name ?? "—"}</div>
              </div>
              <div>
                <div className="ev-body-micro muted">Dealer phone</div>
                <div className="ev-body-mono">{selected.dealer?.phone ?? "—"}</div>
              </div>
            </div>

            {selected.note && (
              <p className="admin-note" style={{ marginTop: 16 }}>
                {selected.note}
              </p>
            )}

            {selected.state === "pending" ? (
              <>
                <h4 style={{ marginTop: 24, marginBottom: 8 }}>
                  <ShieldAlert size={14} /> Confirm against the bank statement
                </h4>
                <div className="admin-field">
                  <label htmlFor="topup-credit">Amount to credit (TZS)</label>
                  <input
                    id="topup-credit"
                    className="ev-input"
                    inputMode="numeric"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value.replace(/[^\d.]/g, ""))}
                  />
                  {amountDiffers && (
                    <span className="ev-body-micro muted">
                      Crediting {tzs(claimedMinor)} instead of the claimed {tzs(selected.amount_minor)}.
                    </span>
                  )}
                </div>
                <div className="admin-field">
                  <label htmlFor="topup-reason">Note for the dealer (optional)</label>
                  <textarea
                    id="topup-reason"
                    className="ev-textarea"
                    rows={3}
                    placeholder="Matched CRDB statement 04/11…"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    style={{ resize: "vertical" }}
                  />
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
                  <button
                    className="ev-btn ev-btn-primary"
                    disabled={decide.isPending || claimedMinor <= 0}
                    onClick={() => decide.mutate({ id: selected.id, decision: "approve" })}
                  >
                    <Check size={16} /> Approve and credit
                  </button>
                  <button
                    className="ev-btn ev-btn-outline"
                    disabled={decide.isPending || reason.trim().length < 5}
                    onClick={() => decide.mutate({ id: selected.id, decision: "reject" })}
                  >
                    <X size={16} /> Reject
                  </button>
                  {reason.trim().length < 5 && (
                    <span className="ev-body-micro muted" style={{ alignSelf: "center" }}>
                      A rejection needs a reason of at least 5 characters.
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div className="admin-detail-grid" style={{ marginTop: 24 }}>
                <div>
                  <div className="ev-body-micro muted">Decided</div>
                  <div className="ev-body">{shortDateTime(selected.decided_at)}</div>
                </div>
                <div>
                  <div className="ev-body-micro muted">Credited</div>
                  <div className="ev-body-mono">{tzs(selected.credited_amount_minor ?? 0)}</div>
                </div>
                {selected.funding_ref && (
                  <div>
                    <div className="ev-body-micro muted">Ledger reference</div>
                    <div className="ev-body-mono">{selected.funding_ref}</div>
                  </div>
                )}
                {selected.decision_reason && (
                  <div>
                    <div className="ev-body-micro muted">Reason</div>
                    <div className="ev-body">{selected.decision_reason}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
